import { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { saveAs } from 'file-saver';
import { api, formatDateTime } from '../../utils/api';
import { rowsToXlsxBlob } from '../../utils/xlsx';
import { buildQuoteExport } from './quote-assessment/buildQuoteExport.js';
import { parseItems, parseItemLine, getSearchUrls, parseSellEntry } from '../../utils/dealConstants';
import { displayCondition } from '../warehouse/conditionDisplay';
import PhotoLightbox from '../shared/PhotoLightbox';
import DealDetailModal from './DealDetailModal';
import DealNotes from './DealNotes';
import { useAssessmentQueue } from './quote-assessment/useAssessmentQueue.js';
import { mergeStructuredItems } from './quote-assessment/mergeStructuredItems.js';
import { isParkedForReview, parkReasonLabel } from './quote-assessment/parkedForReview.js';
import { hasUnpricedItem, itemIsPriced } from './quote-assessment/quotePricing.js';
import MarketCompsPanel from './quote-assessment/MarketCompsPanel.jsx';
import PipelineHealthBanner from './quote-assessment/PipelineHealthBanner.jsx';
import BpBreakdownPanel from './quote-assessment/BpBreakdownPanel.jsx';
import FairPricePanel from './quote-assessment/FairPricePanel.jsx';
import CustomerMessagesPanel from './quote-assessment/CustomerMessagesPanel.jsx';
import TheirPrice from './quote-assessment/TheirPrice.jsx';
import SecondOpinionPanel from './quote-assessment/SecondOpinionPanel.jsx';
import PastItemsPopup from './quote-assessment/PastItemsPopup.jsx';
import ReverseLookupPopup from './quote-assessment/ReverseLookupPopup.jsx';
import IdentityCard from './quote-assessment/IdentityCard.jsx';
import ItemPhotos from './quote-assessment/ItemPhotos.jsx';
import VerdictNotePanel from './quote-assessment/VerdictNotePanel.jsx';
import { useItemIdentifications } from './quote-assessment/useItemIdentifications.js';
import { useAssessmentTimer } from './quote-assessment/useAssessmentTimer.js';
import { verdictChangesWorkerIdentity, splitDisplayName, toVerdictBody, withIdentityConfirmation } from './quote-assessment/identityAgreement.js';
import VerdictApplyDialog from './quote-assessment/VerdictApplyDialog.jsx';
import MergePickerModal from '../catalog/MergePickerModal.jsx';
import ProductDetailModal from '../catalog/ProductDetailModal.jsx';
import { AuthContext } from '../../hooks/useAuth.jsx';
import BuyingRulesPanel from './quote-assessment/BuyingRulesPanel.jsx';
import ConfirmOfferDialog from './quote-assessment/ConfirmOfferDialog.jsx';
import CustomerMessageBox from './quote-assessment/CustomerMessageBox.jsx';
import QuoteReadyMessageDialog from './quote-assessment/QuoteReadyMessageDialog.jsx';
import PairConfirmDialog from './quote-assessment/PairConfirmDialog.jsx';
import ConsignmentConfirmDialog from './quote-assessment/ConsignmentConfirmDialog.jsx';
import CustomerSubmissionDialog from './quote-assessment/CustomerSubmissionDialog.jsx';
import CustomerNotePanel from './quote-assessment/CustomerNotePanel.jsx';
import OpAnswersPanel from './quote-assessment/OpAnswersPanel.jsx';
import WarehouseSuggestion, { isAgreedPrice } from './quote-assessment/WarehouseSuggestion.jsx';
// No email for an in-warehouse OP quote, so its Quote Ready skips the email dialog.
import { isWarehouseDropoffOp } from './quote-assessment/opRouting.js';
import useCustomerSubmission from './quote-assessment/useCustomerSubmission.js';
import { pairableItems } from './quote-assessment/pairable.js';
import RepriceDialog from './quote-assessment/RepriceDialog.jsx';
import AddItemDialog from './quote-assessment/AddItemDialog.jsx';
import ConfirmActionDialog from './quote-assessment/ConfirmActionDialog.jsx';
import { ITEM_CONDITION_OPTIONS } from './quote-assessment/itemConditions.js';
import ReviewDoneDialog, { REVIEW_ACTIONS } from './quote-assessment/ReviewDoneDialog.jsx';
import AssignReviewDialog from './quote-assessment/AssignReviewDialog.jsx';
import { filterQuoteReviewAssignees } from '../../../../shared/users.js';
import { OPENAI_SECOND_OPINION_MODEL } from '../../../../shared/ai-models.js';
import CategoryPicker from './quote-assessment/CategoryPicker.jsx';
import ItemEconomicsBadges from './quote-assessment/ItemEconomicsBadges.jsx';
import { mergeItemSignals, engineFigures } from './quote-assessment/mergeItemSignals.js';
import { secondOpinionTargets } from './quote-assessment/secondOpinionTargets.js';
import { useBuyingRules } from './quote-assessment/useBuyingRules.js';
import { toTelemetryEvent } from './quote-assessment/evaluateBuyingRules.js';
import LinePrices from './LinePrices.jsx';
import './quote-assessment/quote-assessment.css';

const STAGE_QUOTE_READY = '4383256790';
const STAGE_DUPLICATE = '1616888039';
const REJECT_REASONS = [
    'Low confidence in what the item is',
    'Not enough market information to price',
    'Condition is not right',
    'Size - Logistics risk',
    'Too low price or margin possible — we cannot offer more',
    'Low intake category rejection',
];
// The customer text a verdict note carries for `verdict` (spec 20261008_verdict_notes),
// whether the note is that verdict or a Human review suggesting it.
const noteText = (note, verdict, field) =>
    (note && (note.verdict === verdict || note.suggested_verdict === verdict) && note[field]) || '';
// Shortfall reasons (spec 20260813): why we couldn't meet the customer's
// fair-price ask. Unlike REJECT_REASONS these ARE shown to the customer (as
// prose, via the collection page). Values are the ShortfallReason enum.
const SHORTFALL_REASONS = [
    { value: 'item_condition', label: 'Item condition' },
    { value: 'market_demand', label: 'Market demand' },
    { value: 'market_pricing', label: 'Market pricing' },
    { value: 'logistics', label: 'Logistics' },
];
// Round to at most 2 decimals (709 → 709, 188.7799 → 188.78).
const money = n => (n == null || n === '' || Number.isNaN(Number(n))) ? '—' : Math.round(Number(n) * 100) / 100;
const openWin = url => window.open(url, '_blank', 'noopener,noreferrer');

// Normalize any condition string to its canonical DB value (customer label → DB).
// "Like New"/"Open Box" → Mint, "Spares or Repair"/"Not Working" → Broken, else Title Case.
function canonCond(c) {
    if (!c) return '';
    const cl = String(c).toLowerCase().trim();
    if (cl === 'like new' || cl === 'open box') return 'Mint';
    if (cl === 'spares or repair' || cl === 'not working') return 'Broken';
    return cl.replace(/\b\w/g, m => m.toUpperCase());
}

// Names a past deal the way the page heading names the current one: first
// item, then "(and others)". Falls back to `dealname` when no line parses —
// post-cutover that is the reference code, which the row's first column
// already shows, but a legacy row with a real title keeps it.
function pastDealName(d) {
    const names = parseItems(d.parsed_items).map(parseItemLine).filter(Boolean);
    return names.length
        ? `${names[0].name}${names.length > 1 ? ' (and others)' : ''}`
        : (d.dealname || '');
}

// Mirror QuoteRequested's condition badge colours.
function getConditionClass(condition) {
    const c = (condition || '').toLowerCase();
    if (c === 'mint') return 'condition-new';
    if (c === 'excellent') return 'condition-excellent';
    if (c === 'very good') return 'condition-very-good';
    if (c === 'good') return 'condition-good';
    if (c === 'fair') return 'condition-fair';
    if (c === 'poor') return 'condition-poor';
    if (c === 'broken') return 'condition-not-working';
    return 'badge-neutral';
}

export default function QuoteAssessment() {
    const queue = useAssessmentQueue();
    const deal = queue.current;

    // Selection is the item's ID, never its row. A row position stops naming the same
    // item the moment a line is removed — removal REJECTS the item, so the survivors
    // keep their stored indexes while sliding up the list, and every receipt keyed by
    // position then belonged to the neighbour.
    const [selectedItemId, setSelectedItemId] = useState(null);
    // Selecting an item (the row OR its number badge) updates the pricing-receipts
    // panel in place. It deliberately does NOT scroll the page: auto-scrolling down
    // to the receipts jumped the page and buried the item list, which operators
    // found jarring.
    const [images, setImages] = useState([]);
    const [verdictNotes, setVerdictNotes] = useState([]);
    const [verdictNotesError, setVerdictNotesError] = useState(null);
    const [lightbox, setLightbox] = useState(null);
    const [busy, setBusy] = useState(null);        // action name while in flight
    const [error, setError] = useState(null);
    const [downloadingXlsx, setDownloadingXlsx] = useState(false); // bulk-quote Excel export in flight
    const [pastTitle, setPastTitle] = useState(null);
    const [revLookupItem, setRevLookupItem] = useState(null);
    // KG identity cards (Prism spec kg_quote_path §8): the KG's answer beside the
    // worker's, read from one endpoint per quote; bumped after every verdict.
    const [identityVersion, setIdentityVersion] = useState(0);
    // The catalogue product behind a KG answer opens in the Catalog's product
    // modal; its review API is admin-only, so only admins get the link.
    const [openProductId, setOpenProductId] = useState(null);
    const auth = useContext(AuthContext);
    const isAdmin = !!auth?.user?.is_admin;
    // D6 capabilities: canFinalize (Deals Admin / admin) drives the finalise
    // buttons; isReviewer (a restricted Deals Reviewer) swaps them for a "Review
    // done" hand-back + Reassign (spec docs/archive/202609/specs_20260916_quote_review.md).
    // Outside an AuthProvider (isolated unit tests only — the app always wraps
    // this page in one) assume a full operator so the finalise flow still renders.
    const isReviewer = auth ? !!auth.isDealsReviewer : false;
    const canFinalize = auth ? !!auth.canFinalize : true;
    const [reviewDoneOpen, setReviewDoneOpen] = useState(false);
    const [assignOpen, setAssignOpen] = useState(false);
    const [assignees, setAssignees] = useState([]);
    const identifications = useItemIdentifications(deal?.id, identityVersion);
    const [correctFor, setCorrectFor] = useState(null); // { item, card } while the picker is open
    const [verdictAsk, setVerdictAsk] = useState(null); // { item, card, verdict } awaiting record-only vs update
    const [showCustomer, setShowCustomer] = useState(false);
    const [customerDeals, setCustomerDeals] = useState(null);
    const [customerDealsError, setCustomerDealsError] = useState(null);
    const [edit, setEdit] = useState(null);         // { idx, field, value }
    const [rejectOpen, setRejectOpen] = useState(false);
    const [rejectReasons, setRejectReasons] = useState([]);
    const [rejectMessage, setRejectMessage] = useState('');
    const [rejectPreview, setRejectPreview] = useState(null);   // { standard, before, after } | { error }
    // The Quote Ready dialog and its optional message (spec docs/archive/202610/specs_20261013_offer_message.md).
    const [quoteReadyPreview, setQuoteReadyPreview] = useState(null);   // { standard, before, after } | { error }
    const [quoteReadyMessageOpen, setQuoteReadyMessageOpen] = useState(false);
    // The message for the Quote Ready in flight. A ref, not state: it is set and read
    // within one click, across the gate dialogs that may open in between.
    const offerMessageRef = useRef('');
    const [rejectNotice, setRejectNotice] = useState(null);     // rejected but not emailed; survives the move to the next quote
    const [shortfallOpen, setShortfallOpen] = useState(false);
    const [shortfallReasons, setShortfallReasons] = useState([]);
    const [moreInfoOpen, setMoreInfoOpen] = useState(false);
    const [moreInfoOptions, setMoreInfoOptions] = useState({ photosInfo: false, other: false });
    const [otherText, setOtherText] = useState('');
    const [notesOpen, setNotesOpen] = useState(false);
    // "Second opinion — all items" batch (spec: quote-level ChatGPT cross-check):
    // gate is null | { checking:true } (pre-flight status read) | { fire:[items], skipped:n }
    // (the confirm). progress tracks the fire loop; version bumps the open item's
    // SecondOpinionPanel so it reloads once its request is in flight.
    const [secondOpinionGate, setSecondOpinionGate] = useState(null);
    const [secondOpinionProgress, setSecondOpinionProgress] = useState(null); // { done, total }
    const [secondOpinionVersion, setSecondOpinionVersion] = useState(0);
    const [openedDeal, setOpenedDeal] = useState(null);
    const [openingDealId, setOpeningDealId] = useState(null);
    const [receiptsVersion, setReceiptsVersion] = useState(0);
    const bumpReceipts = () => setReceiptsVersion(v => v + 1);
    // The fair-price ask for this quote, if any: undefined while loading, null
    // when never asked (404), else the request. A prior ask (answered OR
    // expired) blocks a re-ask (one ask per quote, spec 20260811).
    const [fairPrice, setFairPrice] = useState(undefined);
    // Bumped on comp-exclusion mark/undo (and after reprices) so the Quote Ready
    // comps_stale warning re-fetches pending counts (spec specs_20260713_comp_exclusions.md).
    const [exclusionsVersion, setExclusionsVersion] = useState(0);
    const [repriceProgress, setRepriceProgress] = useState(null);
    const [boostInfo, setBoostInfo] = useState(null);   // affiliate buy-price boost on this deal
    const [itemCategories, setItemCategories] = useState({}); // MAIN_CAT per quote_item_id (vision pass or manual)
    const [itemSubCategories, setItemSubCategories] = useState({}); // SUB_CAT per quote_item_id — drives the bp_matrix cell
    const [itemCategorySources, setItemCategorySources] = useState({}); // provenance per quote_item_id: 'vision'|'manual'|'auto'|null
    const [categoriesLoaded, setCategoriesLoaded] = useState(false);
    const [diagnosticsItems, setDiagnosticsItems] = useState([]); // engine per-item signals feed (/diagnostics), position-aligned to the spine
    const [confirmOfferOpen, setConfirmOfferOpen] = useState(false);
    const [blockNotice, setBlockNotice] = useState(false); // Quote Ready clicked while a BLOCK rule is active
    const [pairGate, setPairGate] = useState(null); // { items: pairableRows, proceed } while the pair/qty confirm is open
    const [consignGate, setConsignGate] = useState(null); // { defaultSplit } while the consignment terms confirm is open
    const [configDefaultSplit, setConfigDefaultSplit] = useState(null); // Rules-page default seller share (%), or null if unreadable
    const [submissionOpen, setSubmissionOpen] = useState(false); // "what the customer originally submitted" viewer
    // One read of the submission per deal, shared by the Customer note panel and the dialog.
    const submission = useCustomerSubmission(deal?.id, deal?.quote_id);
    const [repriceAsk, setRepriceAsk] = useState(null); // null | { scope: 'item', pos } | { scope: 'quote' }
    // In-page dialogs in place of the browser's prompt()/confirm(), which block page automation.
    const [addItemOpen, setAddItemOpen] = useState(false);
    const [confirmAsk, setConfirmAsk] = useState(null); // null | { title, message, confirmLabel, danger, onConfirm }
    const itemRefs = useRef({});
    // Stamps the "second opinion — all items" pre-flight so a status read that lands
    // after a deal switch cannot reopen the confirm on the wrong quote (same guard the
    // per-item SecondOpinionPanel uses). Bumped on open and on every deal change.
    const secondOpinionGenRef = useRef(0);

    async function openDealById(dealId) {
        if (!dealId || openingDealId) return;
        setOpeningDealId(dealId);
        try {
            const res = await api.get(`/api/deals/${dealId}`);
            if (res.deal) setOpenedDeal(res.deal);
        } catch (e) {
            console.error('Failed to open deal:', e);
        } finally {
            setOpeningDealId(null);
        }
    }

    // The item's id and display fields ride the structured deal.items
    // (projectQuoteItem), the prices and qty ride the blob — merged by position,
    // guarded against mismatch (see mergeStructuredItems).
    const items = deal ? mergeStructuredItems(parseItems(deal.parsed_items).map(parseItemLine).filter(Boolean), deal.items) : [];

    // Bulk (BLK) quotes get a one-click Excel export — a spreadsheet of every
    // item with the operator's full pricing detail, built in the browser from the
    // data already on the page (no backend). Photos ride the KG identity cards.
    async function downloadQuoteExcel() {
        setDownloadingXlsx(true);
        setError(null);
        try {
            const { filename, sheetName, matrix } = buildQuoteExport(deal, items, identifications.cardsById, images);
            const blob = await rowsToXlsxBlob(matrix, { sheetName });
            saveAs(blob, filename);
        } catch (e) {
            setError(`Excel export failed: ${e.message}`);
        } finally {
            setDownloadingXlsx(false);
        }
    }

    // Record how long this operator spends on the quote (visible time only),
    // accumulated per (deal, user) in the backend so time-per-quote and
    // time-per-item can be measured. No UI — see useAssessmentTimer.
    useAssessmentTimer(deal?.id, deal?.reference_code, items.length);

    // Quote-level roll-ups shown above the item spine. Value is Σ(BP × Qty) and
    // saleValue is Σ(SP × Qty) — BP/SP are per-unit buy/sell prices, so a line's
    // contribution multiplies by its Qty (tasks/lessons.md: blob BP/SP are
    // per-unit). Lines with no BP/SP add nothing to that sum, but their units
    // still count toward Total Items.
    const quoteTotals = items.reduce((acc, it) => {
        const qty = Number(it.qty) || 0;
        const bp = Number(it.bp);
        const sp = Number(it.sp);
        acc.units += qty;
        if (!Number.isNaN(bp)) acc.value += bp * qty;
        if (!Number.isNaN(sp)) acc.saleValue += sp * qty;
        return acc;
    }, { value: 0, units: 0, saleValue: 0 });
    // Aggregate margin across the quote: (ΣSP·Qty − ΣBP·Qty) / ΣSP·Qty, to one
    // decimal. Guarded — with no sale value there is no margin to show.
    const quoteMargin = quoteTotals.saleValue > 0
        ? Math.round(((quoteTotals.saleValue - quoteTotals.value) / quoteTotals.saleValue) * 1000) / 10
        : null;
    const totalsChip = (
        <>
            <span className="qa-totals" title="Sum of Buy Price × Qty across every item on this quote">
                <span className="qa-totals-k">Total Quoted Value</span>
                <span className="qa-totals-v">£{money(quoteTotals.value)}</span>
                <span className="qa-totals-dot">·</span>
                <span className="qa-totals-n">{quoteTotals.units} item{quoteTotals.units === 1 ? '' : 's'}</span>
            </span>
            <span className="qa-sale-totals" title="Sum of Sell Price × Qty across every item, with the aggregate margin (ΣSP − ΣBP) / ΣSP">
                <span className="qa-sale-totals-k">Total Estimated Sale Value</span>
                <span className="qa-sale-totals-v">£{money(quoteTotals.saleValue)}</span>
                <span className="qa-sale-totals-dot">·</span>
                <span className="qa-sale-totals-m">Margin: {quoteMargin == null ? '—' : `${quoteMargin}%`}</span>
            </span>
        </>
    );

    // The item the receipts panels are showing. Held by ID and looked up here, so a
    // removal cannot leave the panels pointing at whatever now occupies that row.
    // Falls back to the first line, which is also the initial selection.
    const selectedItem = items.find(it => it.quote_item_id && it.quote_item_id === selectedItemId) ?? items[0] ?? null;
    const selectedId = selectedItem?.quote_item_id ?? null;

    // The category maps arrive keyed by `quote_item_id`, while the rules engine, the
    // recommendation rows and the badges all address an item by its position in THIS
    // list. Projected onto it once, here — a second translation elsewhere is how the
    // two drift out of step.
    const rowCategories = items.map(it => itemCategories[it.quote_item_id] ?? null);
    const rowSubCategories = items.map(it => itemSubCategories[it.quote_item_id] ?? null);
    // Engine signals ride the diagnostics feed, which is keyed by the item's STORED
    // index (no quote_item_id), so it projects by POSITION — guarded against a count
    // mismatch, same contract as the blob merge above (mergeItemSignals). The receipts
    // column shows the read for the SELECTED item, so pick that row's signals here.
    // Each row also carries whether its identity is confirmed (the KG card or the
    // operator's verdict): the consignment gate trusts that over the engine's score.
    const rowSignals = withIdentityConfirmation(mergeItemSignals(items, diagnosticsItems), items, identifications.cardsById);
    const rowEngine = engineFigures(items, diagnosticsItems);
    const selectedSignals = rowSignals[items.indexOf(selectedItem)] ?? null;
    // The verdict note addresses its lines by quote_item_id, not position.
    const signalsById = Object.fromEntries(items.map((it, i) => [it.quote_item_id, rowSignals[i] ?? null]));
    const rules = useBuyingRules(deal, items, rowCategories, categoriesLoaded, rowSubCategories, exclusionsVersion);

    // The Rules-page default seller share preloads the Consignment confirm dialog.
    // Read once (config, not per-deal); on failure the dialog falls back to the
    // house default, so a missing pricing:view never blocks sending an offer.
    useEffect(() => {
        let cancelled = false;
        api.get('/api/pricing/config')
            .then(rows => {
                if (cancelled) return;
                const master = Array.isArray(rows) ? rows.find(r => r.channel === '*') : null;
                const pct = Number(master?.consignment_default_split_pct);
                setConfigDefaultSplit(Number.isFinite(pct) ? pct : null);
            })
            .catch(() => { if (!cancelled) setConfigDefaultSplit(null); });
        return () => { cancelled = true; };
    }, []);

    // The Reject dialog previews the email text. The wording is the same for every
    // customer, so it loads once with the page and the dialog opens with it in place.
    // It comes from the worker's own builder, so it cannot drift from the send.
    useEffect(() => {
        let alive = true;
        api.get('/api/deals/reject-email-template')
            .then(preview => { if (alive) setRejectPreview(preview); })
            .catch(() => { if (alive) setRejectPreview({ error: true }); });
        return () => { alive = false; };
    }, []);
    // The same for the Quote Ready email. Only a finaliser can send it, and the route
    // refuses anyone else.
    useEffect(() => {
        if (!canFinalize) return undefined;
        let alive = true;
        api.get('/api/deals/quote-ready-email-template')
            .then(preview => { if (alive) setQuoteReadyPreview(preview); })
            .catch(() => { if (alive) setQuoteReadyPreview({ error: true }); });
        return () => { alive = false; };
    }, [canFinalize]);

    // Reset per-deal state and load photos when the current deal changes.
    useEffect(() => {
        setSelectedItemId(null); setError(null); setImages([]); setLightbox(null);
        setVerdictNotes([]); setVerdictNotesError(null);
        setShowCustomer(false); setCustomerDeals(null); setEdit(null);
        setBoostInfo(null);
        setItemCategories({});
        setItemSubCategories({});
        setItemCategorySources({});
        setCategoriesLoaded(false);
        setDiagnosticsItems([]);
        setConfirmOfferOpen(false); setBlockNotice(false); setRepriceAsk(null);
        setQuoteReadyMessageOpen(false); offerMessageRef.current = '';
        setAddItemOpen(false); setConfirmAsk(null);
        // Dialog inputs are per-deal: a draft cancelled on one deal must never resurface
        // on another (risk: stale text emailed to the wrong customer).
        setRejectOpen(false); setRejectReasons([]); setRejectMessage('');
        setShortfallOpen(false); setShortfallReasons([]);
        setMoreInfoOpen(false); setMoreInfoOptions({ photosInfo: false, other: false }); setOtherText('');
        setSecondOpinionGate(null); setSecondOpinionProgress(null);
        secondOpinionGenRef.current++; // invalidate any in-flight all-items pre-flight
        if (!deal) return;
        let alive = true;
        api.get(`/api/deals/${deal.id}/item-categories`)
            .then(r => { if (alive) { setItemCategories(r.categories || {}); setItemSubCategories(r.subs || {}); setItemCategorySources(r.sources || {}); setCategoriesLoaded(true); } })
            // Stays not-loaded (rules hold off rather than fire spurious blocks) but the
            // failure must be visible: silently rendering "no category" everywhere reads
            // as data loss to the operator.
            .catch(e => {
                if (!alive) return;
                setItemCategories({}); setItemSubCategories({}); setItemCategorySources({});
                setError(`Item categories failed to load (${e.message}). Refresh the page — Quote Ready stays blocked until categories load.`);
            });
        api.get(`/api/deals/${deal.id}/images`)
            .then(r => { if (alive) setImages(r.images || []); })
            .catch(() => { if (alive) setImages([]); });
        api.get(`/api/deals/${deal.id}/verdict-notes`)
            .then(r => {
                if (!alive) return;
                const notes = r.notes || [];
                setVerdictNotes(notes);
                // The dialogs start from the latest note's text for the same verdict.
                // Set once per quote, and never over text the operator already typed.
                const reject = noteText(notes[0], 'reject', 'rejection_text');
                const moreInfo = noteText(notes[0], 'more_info', 'more_info_text');
                if (reject) setRejectMessage(prev => prev || reject);
                if (moreInfo) {
                    setOtherText(prev => prev || moreInfo);
                    setMoreInfoOptions(p => ({ ...p, other: true }));
                }
            })
            // Shown, not swallowed: an empty panel would read as "no verdict yet".
            .catch(e => { if (alive) setVerdictNotesError(e.message); });
        // Engine signals for the badge cluster — supplementary, so a failure just
        // leaves the rows without signal chips (never blocks the page).
        api.get(`/api/deals/${deal.id}/diagnostics`)
            .then(r => { if (alive) setDiagnosticsItems(r.items || []); })
            .catch(() => { if (alive) setDiagnosticsItems([]); });
        // Auto-apply + surface any affiliate buy-price boost (idempotent + guarded server-side).
        // On first application the deal's BPs change, so refresh parsed_items to show them.
        api.post(`/api/deals/${deal.id}/apply-affiliate-boost`, {})
            .then(async r => {
                if (!alive || !r) return;
                if (r.boosted) {
                    setBoostInfo({ boosted: true, pct: r.pct, label: r.label });
                    if (r.applied) {
                        const fresh = await api.get(`/api/deals/${deal.id}`).catch(() => null);
                        if (alive && fresh?.deal) queue.updateCurrent({ parsed_items: fresh.deal.parsed_items });
                    }
                } else if (r.referral) {
                    setBoostInfo({ boosted: false, referrerName: r.referral.referrerName });
                }
            })
            .catch(() => {});
        return () => { alive = false; };
    }, [deal && deal.id]);

    // The fair-price ask state (spec 20260811). 404 = never asked (null); the
    // latest request (fair-price OR more-info — they share the rails) drives the
    // panel and the per-type button gating below. Only keep a payload that
    // actually looks like one, so a fall-through mock can't fake it.
    useEffect(() => {
        if (!deal?.id) { setFairPrice(null); return; }
        let alive = true;
        setFairPrice(undefined);
        api.get(`/api/deals/${deal.id}/fair-price`)
            .then(r => { if (alive) setFairPrice(r && r.status ? r : null); })
            .catch(() => { if (alive) setFairPrice(null); });
        return () => { alive = false; };
    }, [deal && deal.id]);

    // Item edits are per-ITEM in Prism, not properties of the quote, so each one
    // goes to its own endpoint. They used to rewrite the whole `parsed_items`
    // blob onto the deal, which the property router refuses outright — a blob
    // write cannot be reconciled to item edits — so none of them ever reached
    // the backend.
    //
    // The reply carries the blob rebuilt from what was STORED, which is what
    // re-renders the line. That matters beyond the label: only the price tier
    // matching an item's condition is live, so a regrade shows the new grade's
    // price, or no price when that grade has none yet and the item needs a
    // reprice. `items` comes back too — the name editor works on brand and model
    // separately, and the blob renders them joined.
    const applyItemWrite = useCallback((r) => {
        queue.updateCurrent({ parsed_items: r.parsedItems, condition: r.condition ?? null, items: r.items ?? [] });
    }, [queue]);

    const patchItem = useCallback(async (quoteItemId, fields) => {
        applyItemWrite(await api.patch(`/api/deals/${deal.id}/items/${quoteItemId}`, fields));
    }, [deal, applyItemWrite]);

    const saveCondition = useCallback((quoteItemId, condition) => patchItem(quoteItemId, { condition }), [patchItem]);

    // A verdict on the identity card (Prism spec kg_quote_path rulings 8, 9). The
    // verdict itself is a pinned attachment in the graph, a label for both systems.
    // When the verdict's product differs from what the worker wrote (beyond
    // punctuation, spacing and case), the operator chooses in VerdictApplyDialog:
    // record the verdict alone, or ALSO make the same edit they would type by hand,
    // brand and model patched through patchItem (which clears the item's prices)
    // followed by the reprice dialog. `updateItem` carries that choice; null means
    // not yet asked.
    const postVerdict = useCallback((quoteItemId, body) =>
        api.post(`/api/deals/${deal.id}/items/${quoteItemId}/verdict`, body), [deal]);

    async function applyVerdict(item, card, verdict, updateItem = null) {
        const product = verdict.product ?? null;
        const differs = verdict.kind !== 'reject' && verdictChangesWorkerIdentity(card, product);
        if (differs && updateItem === null) { setVerdictAsk({ item, card, verdict }); return; }
        const changes = differs && updateItem === true;
        const pos = items.findIndex(i => i.quote_item_id === item.quote_item_id);
        const ok = await withBusy(`verdict-${item.quote_item_id}`, async () => {
            await postVerdict(item.quote_item_id, toVerdictBody(verdict));
            if (changes) {
                const { brand, model } = splitDisplayName(product.display_name, product.brand);
                await patchItem(item.quote_item_id, { brand, model });
            }
        });
        // withBusy shows the error and resolves either way; a failed verdict or
        // patch must not refresh the card or ask for a reprice, or the graph and
        // the item contradict each other while the page asks to price it.
        if (!ok) return;
        setIdentityVersion(v => v + 1);
        if (changes && pos >= 0) setRepriceAsk({ scope: 'item', pos });
    }

    function onVerdict(item, card, verdict) {
        if (verdict.kind === 'correct' && !verdict.product && !verdict.newProduct) {
            setCorrectFor({ item, card });
            return;
        }
        return applyVerdict(item, card, verdict);
    }

    // Actions are SERIALISED, not merely labelled busy.
    //
    // Prices are written server-side as read-merge-write across an item's whole
    // condition ladder (G2 replaces `tiers` wholesale), and Prism offers no
    // optimistic-concurrency check. Two overlapping saves therefore lose an
    // update: the second request can read before the first has written, and its
    // merge silently reverts it. SP and BP are adjacent inline editors, so
    // blurring one and immediately editing the other is enough to trigger it —
    // and the value at stake is the buy price.
    //
    // Serialising rather than rejecting is deliberate: the inline editors have
    // already cleared themselves by the time we get here, so dropping the second
    // action would lose the operator's edit while appearing to have saved it.
    //
    // Resolves true when `fn` completed and false when it threw (the error is
    // already on screen): a caller that chains UI state on success checks it.
    const actionChain = useRef(Promise.resolve());

    function withBusy(name, fn, { advance = false } = {}) {
        const run = actionChain.current.then(async () => {
            setBusy(name); setError(null);
            try {
                await fn();
                if (advance) queue.next();
                return true;
            } catch (e) {
                setError(e.message || `${name} failed`);
                return false;
            } finally {
                setBusy(null);
            }
        });
        // Keep the chain alive even if a link rejects, so one failure cannot
        // wedge every later action.
        actionChain.current = run.catch(() => {});
        return run;
    }

    // ---- item edits (blocked while locked) ----
    // The id of the item rendered at line `pos` — the address every read and write
    // on this page travels by. The line and the structured item are PAIRED BY
    // POSITION (see mergeStructuredItems) but never ADDRESSED by it: a position is
    // not an identity, and a line whose id did not come through cannot be edited
    // safely, so it refuses rather than guessing and landing on a neighbour.
    const itemIdOf = (pos) => {
        const quoteItemId = items[pos]?.quote_item_id;
        if (!quoteItemId) {
            throw new Error('This item cannot be edited — reload the page. (Its identity did not come through, and guessing risks editing a different item.)');
        }
        return quoteItemId;
    };

    // Open the two identity boxes showing what the operator can already SEE on
    // the line.
    //
    // Not every item has a brand and model: one identified only by the customer's
    // own words carries just `declared_name`, and `itemDisplayName` falls back to
    // it — so the line shows a name while both structured fields are null.
    // Keying the inputs off brand/model alone opened two empty boxes and the name
    // appeared to vanish. Such an item seeds the MODEL box with the displayed
    // name, which the operator can then split across the two fields.
    function openNameEdit(pos, it) {
        const brand = it.brand ?? '';
        const model = (it.brand || it.model)
            ? (it.model ?? '')
            : (it.declared_name ?? it.name ?? '');
        const colour = it.colour ?? '';
        // The "Pair" in the title is `is_pair` rendered as a display_name
        // segment — seeded from the item's own flag, never parsed off the name.
        const pair = !!it.is_pair;
        // The values as opened, so a blur that changed nothing stays a no-op even
        // though `model` may now hold text that is not the stored `model`.
        return {
            idx: pos, field: 'name',
            brand, model, colour, pair,
            brand0: brand, model0: model, colour0: colour, pair0: pair,
        };
    }

    // Save when focus leaves the identity group AS A WHOLE, not when it moves
    // between the three boxes inside it. Tabbing Brand → Model would otherwise
    // save a half-typed edit, and hanging the blur off one box alone silently
    // loses the edit when the operator clicks away from either of the others.
    function onIdentityBlur(e) {
        // `closest`, not `parentElement`: the Pair checkbox sits one level
        // deeper (inside its label), and its parent alone would treat a move to
        // a sibling box as leaving the group.
        if (e.currentTarget.closest('.item-identity-edit')?.contains(e.relatedTarget)) return;
        saveName();
    }

    async function saveName() {
        if (!edit || edit.field !== 'name') return;
        const brand = (edit.brand ?? '').trim();
        const model = (edit.model ?? '').trim();
        const colour = (edit.colour ?? '').trim();
        const e = edit;
        setEdit(null);

        // ONLY what changed travels. Prism invalidates prices on brand/model
        // being PRESENT in the patch, not on their value differing
        // (quote_item_service: `IDENTITY_FIELDS & set(changes)`), so resending an
        // untouched identity alongside a recolour would delete every tier for a
        // cosmetic edit. Colour is not an identity field, so a colour-only patch
        // keeps the prices.
        //
        // Compared against what the boxes OPENED with, not against the stored
        // fields — the model box may have been seeded from `declared_name`,
        // which would otherwise read as an edit on every blur.
        const patch = {};
        if (brand !== e.brand0) patch.brand = brand || null;
        if (model !== e.model0) patch.model = model || null;
        if (colour !== e.colour0) patch.colour = colour || null;
        // Like colour, `is_pair` is not identity — a toggle keeps the tiers.
        if (!!e.pair !== e.pair0) patch.is_pair = !!e.pair;
        if (Object.keys(patch).length === 0) return;

        // Only guard identity when identity is what moved: a recolour must not
        // be refused for an item that never had a brand or a model.
        const touchesIdentity = 'brand' in patch || 'model' in patch;
        if (touchesIdentity && !brand && !model) {
            setError('An item needs a brand or a model.');
            return;
        }
        await withBusy('edit', () => patchItem(itemIdOf(e.idx), patch));
    }
    async function saveQty() {
        if (!edit || edit.field !== 'qty') return;
        const quantity = Number(edit.value);
        const e = edit; setEdit(null);
        if (!Number.isInteger(quantity) || quantity < 1) { setError('Quantity must be a whole number of 1 or more.'); return; }
        if (quantity === items[e.idx]?.qty) return;
        await withBusy('edit', () => patchItem(itemIdOf(e.idx), { quantity }));
    }
    async function removeItem(pos) {
        await withBusy('remove', async () => {
            applyItemWrite(await api.delete(`/api/deals/${deal.id}/items/${itemIdOf(pos)}`));
        });
        // No selection fix-up: the selection is an ID, so removing the selected item
        // simply stops matching and the panels fall back to the first line.
    }
    // The operator types brand and model separately in AddItemDialog, so nothing has to
    // guess a brand boundary ("Bowers & Wilkins CM5"), and the item gets its condition
    // (the one the customer submitted) from the start.
    async function addItem(fields) {
        setAddItemOpen(false);
        await withBusy('add', async () => {
            applyItemWrite(await api.post(`/api/deals/${deal.id}/items`, fields));
        });
    }

    // ---- pricing ----
    async function repriceItem(pos, searchMode) {
        await withBusy(`reprice-${pos}`, async () => {
            const r = await api.post(`/api/deals/${deal.id}/reprice-item`, { quote_item_id: itemIdOf(pos), searchMode });
            if (r.newParsedItems) queue.updateCurrent({ parsed_items: r.newParsedItems });
        });
        bumpReceipts();
        setExclusionsVersion(v => v + 1); // reprice resolves pending exclusion counts
    }
    async function repriceQuote(searchMode) {
        setBusy('reprice-quote'); setError(null);
        const failures = [];
        const total = items.length;
        for (let i = 0; i < total; i++) {
            setRepriceProgress({ done: i, total });
            try {
                const r = await api.post(`/api/deals/${deal.id}/reprice-item`, { quote_item_id: itemIdOf(i), searchMode });
                if (r.newParsedItems) queue.updateCurrent({ parsed_items: r.newParsedItems });
            } catch (e) { failures.push(`#${i + 1}: ${e.message}`); }
        }
        if (failures.length) setError(`Reprice issues — ${failures.join('; ')}`);
        bumpReceipts();
        setRepriceProgress(null);
        setBusy(null);
    }
    // Guard against typo overpayments: any increase of more than 10% from the current value
    // must be confirmed. Returns the question to ask, or null when no confirmation is needed.
    function bigIncreaseQuestion(field, oldRaw, newVal) {
        const oldVal = Number(oldRaw);
        if (!Number.isFinite(oldVal) || oldVal <= 0) return null;   // no meaningful % vs a blank/zero value
        if (newVal <= oldVal * 1.1) return null;
        const pct = Math.round(((newVal - oldVal) / oldVal) * 100);
        return `You are increasing ${field} by ${pct}% (+£${money(newVal - oldVal)}, from £${money(oldVal)} to £${money(newVal)}). Are you sure?`;
    }
    // Run `commit` now, or after the operator confirms a >10% increase in the page.
    function commitAfterIncreaseCheck(field, oldRaw, newVal, commit) {
        const question = bigIncreaseQuestion(field, oldRaw, newVal);
        if (!question) return commit();
        setConfirmAsk({ title: `Raise ${field}?`, message: question, confirmLabel: 'Yes, raise it', onConfirm: commit });
    }
    async function editSp(pos, value) {
        // Accepts a pair ("100/41") as well as a lone SP — the pair is the only
        // way to price an item with no price yet (its first tier needs both
        // halves at once, and the engine has no factors for a never-priced item).
        const entry = parseSellEntry(value);
        if (!entry) { setEdit(null); return; }
        const { sp, bp } = entry;
        if (bp != null && bp > sp) {
            setEdit(null);
            setError(`BP £${money(bp)} is above the SP £${money(sp)} — we'd pay more than we plan to sell it for.`);
            return;
        }
        setEdit(null);
        return commitAfterIncreaseCheck('SP', items[pos]?.sp, sp, async () => {
            await withBusy('sp', async () => {
                const r = await api.post(`/api/deals/${deal.id}/items/${itemIdOf(pos)}/sell-price`, bp != null ? { sp, bp } : { sp });
                if (r.parsedItems) queue.updateCurrent({ parsed_items: r.parsedItems });
            });
            bumpReceipts();
        });
    }
    async function editBp(pos, value) {
        const bp = Number(value);
        if (!Number.isFinite(bp) || bp < 0) { setEdit(null); return; }
        // Buying above the selling price is a guaranteed loss — refuse, don't
        // confirm. Checked before confirmBigIncrease so the operator gets the
        // real reason, not a percentage prompt they can wave through.
        // An unpriced item has no SP to compare against yet: the BP saves and the
        // quote stays gated until the SP is set.
        const sp = Number(items[pos]?.sp);
        if (itemIsPriced(items[pos]) && bp > sp) {
            setEdit(null);
            setError(`BP £${money(bp)} is above the SP £${money(sp)} — we'd pay more than we plan to sell it for. Lower the BP, or raise the SP first.`);
            return;
        }
        setEdit(null);
        return commitAfterIncreaseCheck('BP', items[pos]?.bp, bp, () => withBusy('bp', async () => {
            const r = await api.post(`/api/deals/${deal.id}/items/${itemIdOf(pos)}/buy-price`, { bp });
            if (r.parsedItems) queue.updateCurrent({ parsed_items: r.parsedItems });
        }));
    }

    // ---- item categories (main mandatory before Quote Ready; sub drives the matrix) ----
    async function changeItemCategory(pos, mainCategory, subCategory) {
        const prev = itemCategories;
        const prevSub = itemSubCategories;
        const prevSource = itemCategorySources;
        let quoteItemId;
        try { quoteItemId = itemIdOf(pos); } catch (e) { setError(e.message); return; }
        setItemCategories({ ...itemCategories, [quoteItemId]: mainCategory });   // optimistic
        setItemSubCategories({ ...itemSubCategories, [quoteItemId]: subCategory });
        setItemCategorySources({ ...itemCategorySources, [quoteItemId]: 'manual' });   // manual pick always wins — clears any 'auto' badge
        try {
            // ONE address for BOTH stores. The dropdown reads the pricing D1 table
            // while the item's TITLE renders Prism's own category column, and the
            // correction used to carry a line index for one and a stored index for
            // the other — so on a quote that had lost a line, one of the two landed
            // on the wrong item.
            const r = await api.put(`/api/deals/${deal.id}/item-categories/${quoteItemId}`,
                { main_category: mainCategory, sub_category: subCategory });
            if (r?.parsedItems) applyItemWrite(r);
        } catch (e) {
            setItemCategories(prev);
            setItemSubCategories(prevSub);
            setItemCategorySources(prevSource);
            setError(`Category update failed: ${e.message}`);
        }
    }

    // Rule hits and recommendation rows name an item by its position in `items`, so
    // that is what arrives here; the selection it sets is the item's id.
    function jumpToItem(pos) {
        setSelectedItemId(items[pos]?.quote_item_id ?? null);
        itemRefs.current[pos]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    // ---- quote actions (blocking, advance on success) ----
    const setStage = (dealstage, customerMessage) => api.patch(`/api/deals/${deal.id}/status`,
        customerMessage ? { dealstage, customer_message: customerMessage } : { dealstage });
    // Defence in depth behind the disabled buttons: Quote Ready and Consignment
    // both commit a price to the customer, so refuse them (visibly) while any item
    // is unpriced — a £0 offer must never slip through, even via a future refactor.
    // Reject commits no price, so it is deliberately NOT gated here.
    function blockedForNoPrice() {
        if (!hasUnpricedItem(items)) return false;
        setError('This quote has an item with no price. Price every item (or re-run pricing) before Quote Ready or Consignment.');
        return true;
    }
    // The pairable (speaker-type) items on this quote, shaped for PairConfirmDialog.
    // The gate reads the SUB-category the operator sees (itemSubCategories) and the
    // display name the row shows, so "pairable" here means exactly what the page shows.
    function pairableOnQuote() {
        return pairableItems(
            items,
            (it) => it.display_name || it.name,
            (it) => itemSubCategories[it.quote_item_id] ?? null,
            (it) => it.quote_item_id,
        );
    }

    // Front an offer-committing action (Quote Ready / Consignment) with the pair/qty
    // confirmation when the quote holds a speaker-type item; otherwise proceed straight
    // through. `proceed` runs after the operator confirms (see onPairConfirm).
    function withPairGate(proceed) {
        const pairable = pairableOnQuote();
        if (pairable.length > 0) { setPairGate({ items: pairable, proceed }); return; }
        proceed();
    }

    // Persist any pair/qty fixes the operator made in the dialog, then hand off to the
    // action's own next step. Runs the patches OUTSIDE withBusy so `proceed` (which
    // starts its own withBusy) is sequenced after them, never nested inside — a nested
    // withBusy would wait on the chain link it is already running under and wedge.
    // Editing pair/qty cannot change which buying rules fire (BLOCK = missing category,
    // WARN = per-unit matrix economics), so running the rules gate afterwards with the
    // current `rules` object is correct.
    async function onPairConfirm(edits) {
        const proceed = pairGate?.proceed;
        if (edits.length > 0) {
            setBusy('pair-confirm'); setError(null);
            try {
                for (const e of edits) await patchItem(e.itemId, { is_pair: e.is_pair, quantity: e.quantity });
            } catch (err) {
                setError(err.message || 'Saving the pair/quantity fix failed — try again.');
                setBusy(null);
                return; // keep the dialog open so the fix is not lost
            }
            setBusy(null);
        }
        setPairGate(null);
        proceed?.();
    }

    // Quote Ready is gated by the buying rules: BLOCK hits keep the button clickable but
    // intercept the click (explain + log 'blocked'); WARN hits require the confirm dialog.
    function runRulesGate() {
        if (rules.blockHits.length > 0) {
            setBlockNotice(true);
            rules.logRuleEvents(rules.blockHits.map(h => toTelemetryEvent(h, 'blocked')))
                .catch(e => console.error('buying-rules blocked telemetry failed:', e));
            return;
        }
        if (rules.warnHits.length > 0) { setConfirmOfferOpen(true); return; }
        confirmQuoteReady();
    }
    function actQuoteReady() {
        if (blockedForNoPrice()) return;
        if (!rules.ready) {
            setError(rules.configError
                ? 'Buying-rules config failed to load — reload the page before sending Quote Ready.'
                : 'Buying rules are still loading — try again in a moment.');
            return;
        }
        withPairGate(runRulesGate);
    }
    // The Quote Ready dialog's Continue: the message may be empty, which also clears
    // whatever an earlier click left behind.
    function actQuoteReadyWithMessage(message) {
        setQuoteReadyMessageOpen(false);
        offerMessageRef.current = message;
        actQuoteReady();
    }
    // Fires on every confirm (with or without warnings): active warns → 'overridden',
    // previously-shown-but-inactive rules → 'resolved' (worker computes the diff).
    // Shortfall (spec 20260813): match the customer's answered fair-price ask to
    // our per-item offer. `it.bp` is the current-condition tier BP at Quote Ready
    // (committed is still null, so surfaced == offer == tier) — exactly what the
    // backend reconcile compares against, so the modal trigger and the write agree.
    const shortfallAsks = new Map(
        (fairPrice && fairPrice.status === 'answered' && fairPrice.request_type !== 'more_info'
            ? (fairPrice.items || [])
            : []
        ).filter(a => a.asking_price_cents != null).map(a => [a.quote_item_id, a.asking_price_cents])
    );
    const hasShortfallAsk = shortfallAsks.size > 0;
    // Reconcile whenever the quote has EVER had an ask (any type/status): a prior
    // answered fair-price round may have written shortfall rows that a later
    // more-info ask (which carries no price) must still clear. The modal, in
    // contrast, opens only for the current fair-price shortfall set below.
    const hasAsk = fairPrice != null;
    function shortfallItems() {
        if (!hasShortfallAsk) return [];
        return items.filter(it => {
            const ask = it.quote_item_id ? shortfallAsks.get(it.quote_item_id) : undefined;
            return ask != null && (ask / 100) > Number(it.bp || 0);
        });
    }
    const toggleShortfallReason = (value) => setShortfallReasons(prev =>
        prev.includes(value) ? prev.filter(r => r !== value) : [...prev, value]);

    function confirmQuoteReady() {
        setConfirmOfferOpen(false);
        // A shortfall needs the operator's reason(s); otherwise reconcile directly.
        if (shortfallItems().length > 0) { setShortfallOpen(true); return; }
        finishQuoteReady([]);
    }
    function submitShortfall() {
        if (!shortfallReasons.length) return;
        const reasons = shortfallReasons;
        setShortfallOpen(false);
        setShortfallReasons([]);
        finishQuoteReady(reasons);
    }
    // Unconditional reconcile: whenever the quote has had an ask, POST the reasons
    // (empty when nothing is short now) BEFORE the stage move, so a met-now — or
    // asked-then-more-info — item's stale acknowledgment is cleared. Never asked →
    // no rows can exist, skip the call.
    function finishQuoteReady(reasons) {
        // Read now: the per-deal reset clears the ref if the queue moves during the awaits.
        const customerMessage = offerMessageRef.current;
        withBusy('quote-ready', async () => {
            await rules.logRuleEvents(rules.warnHits.map(h => toTelemetryEvent(h, 'overridden')), { mark_resolved: true })
                .catch(e => console.error('buying-rules override telemetry failed:', e));
            if (hasAsk) {
                await api.post(`/api/deals/${deal.id}/shortfall-reason`, { reasons });
            }
            await setStage(STAGE_QUOTE_READY, customerMessage);
        }, { advance: true });
    }
    const actDuplicate = () => withBusy('duplicate', () => setStage(STAGE_DUPLICATE), { advance: true });
    const consignmentProceed = (splitPct, customerMessage) => withBusy('consignment',
        () => api.post(`/api/deals/${deal.id}/consignment`,
            customerMessage ? { split_pct: splitPct, customer_message: customerMessage } : { split_pct: splitPct }), { advance: true });
    // The offer email text for this quote, for the confirm dialog's preview.
    const dealId = deal?.id;
    const loadConsignmentPreview = useCallback(
        (splitPct) => api.get(`/api/deals/${dealId}/consignment-email-preview?split_pct=${splitPct}`), [dealId]);
    // Preload the confirm dialog with an existing negotiated split if the deal
    // carries one (whole 1–99), else the Rules-page default, else the house 70.
    // The operator's confirmed value is what gets sent (consignmentProceed).
    function openConsignmentDialog() {
        const existing = Number(deal.consignment_split_pct);
        const preload = Number.isInteger(existing) && existing >= 1 && existing <= 99
            ? existing
            : (configDefaultSplit ?? 70);
        setConsignGate({ defaultSplit: preload });
    }
    function onConsignmentConfirm(splitPct, customerMessage) { setConsignGate(null); consignmentProceed(splitPct, customerMessage); }
    function actConsignment() { if (blockedForNoPrice()) return; withPairGate(openConsignmentDialog); }
    // Ask the seller their own price (spec 20260811). Commits no price, so it is
    // NOT gated on pricing (unlike Quote Ready / Consignment); it parks the quote
    // and leaves the queue, so advance to the next. A prior fair-price ask (any
    // status) disables the button below; a more-info ask does not (it shares the
    // rails but is a different type) — see the ask-state derivation near the buttons.
    const actAskFairPrice = () => withBusy('fairprice',
        () => api.post(`/api/deals/${deal.id}/fair-price/request`), { advance: true });
    // Reviewer hand-back and (re)assignment (spec docs/archive/202609/specs_20260916_quote_review.md).
    const actReviewDone = () => setReviewDoneOpen(true);
    async function onReviewDoneConfirm({ comment, suggested_action }) {
        setReviewDoneOpen(false);
        const ok = await withBusy('review-done',
            () => api.post(`/api/deals/${deal.id}/review-done`, { comment, suggested_action }), { advance: true });
        if (ok) window.dispatchEvent(new Event('quote-review:changed'));
    }
    async function openAssignDialog() {
        try {
            const res = await api.get('/api/users');
            setAssignees(filterQuoteReviewAssignees(res));
        } catch { setAssignees([]); }
        setAssignOpen(true);
    }
    async function onAssignConfirm({ assignee_user_id, assignee_name }) {
        setAssignOpen(false);
        const ok = await withBusy('assign-review',
            () => api.post(`/api/deals/${deal.id}/assign-review`, { assignee_user_id, assignee_name }), { advance: true });
        if (ok) window.dispatchEvent(new Event('quote-review:changed'));
    }
    // Full engine re-run via the pricing worker's /internal/requeue — the ONLY action that
    // revives a parked quote. The item-level reprice above calls /manual-price, which
    // prices one existing item and clears no queue state, so a parked quote stays parked
    // however often it is pressed (live confusion on d35c922c, 2026-07-30). The requeue
    // clears all three eligibility halves: item tiers, Prism exclusion, pricing-worker D1.
    function actRequeue() {
        setConfirmAsk({
            title: 'Re-run pricing on this quote?',
            message: 'This clears EVERY price on the quote and prices all items again from scratch. '
                + 'Vision re-reads the photos, and undeclared items it finds there may be added '
                + 'as new lines. The quote leaves this queue until the engine finishes.',
            confirmLabel: 'Re-run pricing',
            danger: true,
            onConfirm: () => withBusy('requeue', async () => {
                const r = await api.post(`/api/deals/${deal.id}/requeue`, { quote_id: deal.quote_id });
                // The worker reports which of the three halves failed — a partial success must
                // not read as re-queued, or the operator waits on a quote that never reprices.
                if (r?.ok === false) throw new Error(r.error || 'Re-queue failed');
            }, { advance: true }),
        });
    }
    const toggleRejectReason = (reason) => setRejectReasons(prev =>
        prev.includes(reason) ? prev.filter(r => r !== reason) : [...prev, reason]);
    async function submitReject() {
        if (!rejectReasons.length) return;
        // No price gate here: rejecting a quote commits no price to the customer,
        // so an unpriced (or NO_MARKET_DATA-parked) quote can still be rejected.
        // Quote Ready and Consignment stay gated in blockedForNoPrice.
        setRejectOpen(false); setRejectNotice(null);
        const quoteName = deal.dealname || deal.id;
        let result;
        const ok = await withBusy('reject', async () => {
            result = await api.post(`/api/deals/${deal.id}/reject`, { reasons: rejectReasons, customer_message: rejectMessage.trim() });
        }, { advance: true });
        // A failed reject keeps the reasons and the message for the retry.
        if (!ok) return;
        setRejectReasons([]); setRejectMessage('');
        // The quote is rejected even when the email did not go. Say so, or the
        // customer hears nothing and nobody knows.
        if (result?.emailSent === false) {
            setRejectNotice(result.email
                ? `${quoteName} was rejected, but the email to ${result.email} was not sent (${result.emailError || 'send failed'}). Email the customer by hand.`
                : `${quoteName} was rejected, but no email was sent: no email address was found for this customer.`);
        }
    }
    async function submitMoreInfo() {
        if (!moreInfoOptions.photosInfo && !moreInfoOptions.other) { setError('Select at least one option.'); return; }
        if (moreInfoOptions.other && !otherText.trim()) { setError('Enter the additional information required.'); return; }
        setMoreInfoOpen(false);
        await withBusy('more-info', () => api.post(`/api/deals/${deal.id}/more-info`, {
            photosInfo: moreInfoOptions.photosInfo,
            otherText: moreInfoOptions.other ? otherText.trim() : null,
        }), { advance: true });
        setMoreInfoOptions({ photosInfo: false, other: false });
        setOtherText('');
    }
    // ---- second opinion, all items ----
    // A quote-level ChatGPT cross-check that reuses the per-item endpoint and panels:
    // it fires the SAME request the per-item SecondOpinionPanel fires, once per item
    // that still needs one, and each item's result surfaces in its own panel.
    //
    // Opening the confirm first reads every addressable item's latest status so the
    // dialog can show an honest count and cost — we neither re-pay for a done item
    // nor double-fire one already running (secondOpinionTargets encodes the rule).
    async function openSecondOpinionAll() {
        const gen = ++secondOpinionGenRef.current;
        const addressable = items.filter(it => it.quote_item_id);
        setSecondOpinionGate({ checking: true });
        setError(null);
        try {
            const entries = await Promise.all(addressable.map(it =>
                api.get(`/api/deals/${deal.id}/items/${it.quote_item_id}/second-opinion`)
                    .then(r => [it.quote_item_id, r?.status])
                    // Unreadable status → allow a fresh ask rather than hide the item.
                    .catch(() => [it.quote_item_id, 'error'])
            ));
            if (secondOpinionGenRef.current !== gen) return; // deal switched mid-check
            const statusByItemId = Object.fromEntries(entries);
            const fire = secondOpinionTargets(addressable, statusByItemId);
            setSecondOpinionGate({ fire, skipped: addressable.length - fire.length });
        } catch (e) {
            if (secondOpinionGenRef.current !== gen) return;
            setSecondOpinionGate(null);
            setError(e.message || 'Could not check existing second opinions.');
        }
    }

    // Kick off the request for every target. Each POST returns immediately (the worker
    // runs the ~2-min call in the background), so firing in parallel is safe and `busy`
    // clears in seconds — the operator is not locked out while ChatGPT researches.
    async function fireSecondOpinionAll() {
        const fire = secondOpinionGate?.fire || [];
        setSecondOpinionGate(null);
        if (!fire.length) return;
        setBusy('second-opinion-all'); setError(null);
        setSecondOpinionProgress({ done: 0, total: fire.length });
        const failures = [];
        await Promise.all(fire.map(async (it) => {
            try {
                await api.post(`/api/deals/${deal.id}/items/${it.quote_item_id}/second-opinion`, { searchMode: 'auto' });
            } catch (e) { failures.push(`${it.name}: ${e.message}`); }
            finally { setSecondOpinionProgress(p => p ? { ...p, done: p.done + 1 } : p); }
        }));
        setSecondOpinionProgress(null);
        setBusy(null);
        if (failures.length) {
            // Stay on the quote so the operator sees which requests didn't start (the
            // ones that did keep running); refresh the open panel to show those.
            setError(`Some second opinions couldn't be started — ${failures.join('; ')}`);
            setSecondOpinionVersion(v => v + 1);
            return;
        }
        // Everything started. The research runs in the background, so move on to the
        // next quote — this one stays in the queue (skip, not next) so its results are
        // here when the operator comes back. Mirrors the per-item panel's
        // "Do the next item in the meantime ▶".
        queue.skip();
    }

    // The fetch, separate from the toggle — Retry must not close the panel it
    // is rendered inside.
    function fetchCustomerDeals() {
        if (!deal.seller_email) return;
        setCustomerDealsError(null);
        setCustomerDeals(null);
        api.get(`/api/deals/customer-deals?email=${encodeURIComponent(deal.seller_email)}`)
            .then(r => setCustomerDeals(r.deals || r.results || []))
            // NOT `setCustomerDeals([])`: an empty list renders as "No other
            // deals", so a failed lookup used to tell the operator this customer
            // has no history — a statement about the data, made from an error.
            // The panel says it could not ask instead.
            .catch(e => setCustomerDealsError(e.message || 'the lookup failed'));
    }

    function loadCustomer() {
        setShowCustomer(s => !s);
        if (!customerDeals && !customerDealsError) fetchCustomerDeals();
    }

    if (queue.loading) return <div className="qa-page"><p className="qa-muted">Loading queue…</p></div>;
    if (queue.error) return <div className="qa-page"><p className="qa-error">{queue.error}</p></div>;
    if (!deal) return <div className="qa-page qa-empty"><h2>All caught up ✓</h2><p className="qa-muted">{isReviewer ? 'No quotes are assigned to you for review.' : 'No priced quotes awaiting assessment.'}</p></div>;

    const navDisabled = !!busy;
    // Quote Ready and Consignment each commit a price to the customer, so they are
    // blocked until every item is priced. A NO_MARKET_DATA park sets every item to
    // £0, which is exactly the state that must not become a £0 offer. Reject commits
    // no price, so it stays enabled regardless (gated only by `busy`).
    const quoteUnpriced = hasUnpricedItem(items);
    const actionsDisabled = navDisabled || quoteUnpriced;
    // The fair-price and More Info asks share one row on the fair-price rails
    // (spec 20260812), so each button must key off the ask's TYPE, not merely its
    // existence: a more-info ask must not lock out the fair-price ask, and vice
    // versa. While either ask is still open, both are blocked — a quote never has
    // two asks open at once (the backend enforces the same mutex).
    const askOpen = fairPrice != null && fairPrice.status === 'requested';
    const askedMoreInfo = fairPrice != null && fairPrice.request_type === 'more_info';
    const askedFairPrice = fairPrice != null && fairPrice.request_type !== 'more_info';
    const fairPriceDisabled = navDisabled || askedFairPrice || (askOpen && askedMoreInfo);
    const moreInfoDisabled = navDisabled || askedMoreInfo || (askOpen && askedFairPrice);
    const noPriceTitle = quoteUnpriced
        ? 'Blocked — an item has no price. Price every item or re-run pricing first.'
        : undefined;
    const isReturn = (deal.customer_deal_count || 0) > 1;
    // Bulk quotes carry a BLK- reference code (the same marker AllDeals uses); only
    // they get the Excel export, matching the old HubSpot bulk-export affordance.
    const isBulk = !!deal.reference_code?.startsWith('BLK-');

    // The heading names the customer's gear the same way the quote email's
    // subject does (`quoteDisplayName`, workers/src/services/quoteEmail.js):
    // first item, then "(and others)" — so an operator on the phone reads the
    // words the customer has in their inbox, not a UUID. Both sides take the
    // name from `itemDisplayName`, this one via the `parsed_items` blob it
    // builds, so the page and the email cannot name the same item differently.
    // The reference code is not repeated here — the row below already carries
    // it. Falls back to the id when no line parses; a title is not worth
    // blanking over.
    const headingName = items.length
        ? `${items[0].name}${items.length > 1 ? ' (and others)' : ''}`
        : deal.id;

    // What the receipts panels are told this item is called. Both values come
    // from the STRUCTURED item, never from splitting the blob's rendered name:
    // `display_name` is Prism's single definition of how an item is spelled, and
    // the verbatim search term is the identity alone. Deriving the second from
    // the first is what printed the same title twice in the second-opinion
    // dialog (pricing-worker `docs/specs_20260727_item_naming.md` §1).
    //
    // The blob fallback keeps a quote whose structured items did not come
    // through rendering exactly as it does today.
    const selectedDisplayName = selectedItem?.display_name
        || (selectedItem?.details ? `${selectedItem.name} - ${selectedItem.details}` : selectedItem?.name) || '';
    const selectedSearchTerm = [selectedItem?.brand, selectedItem?.model].filter(Boolean).join(' ')
        || selectedDisplayName;

    return (
        <div className="qa-page">
            <PipelineHealthBanner />
            {/* TOP BAR */}
            <div className="qa-topbar card">
                <div className="qa-tb-row">
                    <h2 className="qa-title">Quote Assessment — {headingName}</h2>
                    <div className="qa-nav">
                        <button className="btn btn-sm" disabled={navDisabled} onClick={queue.back}>◀ Back</button>
                        <span className="badge badge-neutral">{queue.position} of {queue.total}</span>
                        <button className="btn btn-sm" disabled={navDisabled} onClick={queue.skip}>Skip ▶</button>
                    </div>
                </div>
                {isParkedForReview(deal) && (
                    <div className="qa-tb-row" style={{ background: 'var(--color-error-bg, #fdecea)', borderRadius: 6, padding: '6px 10px' }}>
                        <span className="badge badge-error">Parked — {parkReasonLabel(deal.excluded_reason)}</span>
                        <span className="qa-muted" style={{ marginLeft: 8 }}>
                            {deal.excluded_reason || 'The engine parked this quote for manual review.'}
                            {' '}The engine will not retry it — price it or re-run pricing from here.
                        </span>
                        <button className="btn btn-sm" style={{ marginLeft: 'auto' }}
                            disabled={busy !== null} onClick={actRequeue}
                            title="Clear all prices and send the whole quote back through the engine — the only action that un-parks it">
                            {busy === 'requeue' ? 'Re-queuing…' : '↻ Re-run pricing'}
                        </button>
                    </div>
                )}
                <div className="qa-tb-row">
                    <div className="qa-cust">
                        <strong>{deal.dealname || deal.id}</strong>
                        <span className="qa-muted">{deal.seller_email}</span>
                        <span className="qa-muted" title="Quote created">🕒 {formatDateTime(deal.createdate)}</span>
                        <span className={`badge ${isReturn ? 'badge-warning' : 'badge-success'}`}>
                            {isReturn ? `↺ Return${deal.previous_quote_date ? ' · ' + String(deal.previous_quote_date).slice(0, 10) : ''}` : '✨ New customer'}
                        </span>
                        {queue.siblingCount > 0 && (
                            <span className="badge badge-warning"
                                title={`This customer has ${queue.siblingCount} more quote${queue.siblingCount === 1 ? '' : 's'} waiting in this queue`}>
                                ⚠ +{queue.siblingCount} more in queue
                            </span>
                        )}
                        <button className="btn btn-xs" onClick={loadCustomer}>▾ past deals</button>
                    </div>
                </div>
                <div className="qa-tb-row">
                    {totalsChip}
                    {isBulk && (
                        <button className="btn btn-sm" style={{ marginLeft: 'auto' }}
                            disabled={downloadingXlsx} onClick={downloadQuoteExcel}
                            title="Download this bulk quote as an Excel spreadsheet">
                            {downloadingXlsx ? 'Preparing…' : '⬇ Download Excel'}
                        </button>
                    )}
                </div>
                {showCustomer && (
                    <div className="qa-cust-list">
                        {customerDealsError && (
                            <span className="qa-error" role="alert">
                                Could not load past deals — {customerDealsError}.{' '}
                                <button className="btn btn-xs" onClick={fetchCustomerDeals}>Retry</button>
                            </span>
                        )}
                        {!customerDealsError && customerDeals === null && <span className="qa-muted">Loading…</span>}
                        {customerDeals && customerDeals.length === 0 && <span className="qa-muted">No other deals.</span>}
                        {customerDeals && customerDeals.map((d, i) => (
                            <div key={i} className="qa-cust-row">
                                <span className="qa-cust-id qa-link" onClick={() => openDealById(d.hubspot_deal_id || d.id)}>
                                    {openingDealId === (d.hubspot_deal_id || d.id) ? '…' : (d.reference_code || d.hubspot_deal_id || d.id || '—')}
                                </span>
                                <span>{pastDealName(d)}</span>
                                <span className="qa-muted">{d.stageName || d.deal_stage_label || ''}</span>
                                <span className="qa-muted">{(d.createdate || d.deal_create_date || '').slice(0, 10)}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {error && <div className="qa-error qa-banner" role="alert">{error}</div>}
            {rejectNotice && (
                <div className="qa-error qa-banner" role="alert" data-testid="reject-email-notice">
                    {rejectNotice}{' '}
                    <button className="qa-rule-jump" onClick={() => setRejectNotice(null)}>Dismiss</button>
                </div>
            )}

            {blockNotice && rules.blockHits.length > 0 && (
                <div className="qa-error qa-banner" role="alert">
                    Quote Ready is blocked — {rules.blockHits.length} item{rules.blockHits.length !== 1 ? 's' : ''} without a main category.{' '}
                    <button className="qa-rule-jump" onClick={() => jumpToItem(rules.blockHits[0].itemIndex)}>
                        Go to item #{rules.blockHits[0].itemIndex + 1}
                    </button>
                </div>
            )}

            {boostInfo && boostInfo.boosted && (
                <div className="qa-banner" style={{ background: '#e8f5e9', border: '1px solid #3BB273', color: '#1b5e20' }}>
                    💷 Affiliate boost applied: <strong>+{boostInfo.pct}%</strong> on every buy price
                    {boostInfo.label ? <> — <strong>{boostInfo.label}</strong></> : null}. The buy prices below include the boost.
                </div>
            )}

            {boostInfo && !boostInfo.boosted && boostInfo.referrerName && (
                <div className="qa-banner" style={{ background: '#eef4fb', border: '1px solid #5b8def', color: '#1f3a66' }}>
                    🤝 Referred customer — introduced by <strong>{boostInfo.referrerName}</strong>.
                </div>
            )}

            {/* Second-review status banner (spec docs/archive/202609/specs_20260916_quote_review.md) */}
            {!isReviewer && deal.review_status === 'assigned' && (
                <div className="qa-banner" role="status" style={{ marginBottom: 8 }}>
                    <span className="qa-wait-hd">⚠ WAIT</span>
                    🔍 Assigned for review by {deal.review_assigned_by_name || '—'} — under review by {deal.review_assignee_name || '—'} since {deal.review_assigned_at ? formatDateTime(deal.review_assigned_at) : '—'}
                </div>
            )}
            {!isReviewer && deal.review_status === 'reviewed' && (
                <div className="qa-banner qa-banner-reviewed" role="status" style={{ marginBottom: 8 }}>
                    <span className="qa-reviewed-hd">✅ Review done</span>
                    <div className="qa-reviewed-line">
                        Reviewed by <strong>{deal.review_assignee_name || '—'}</strong> · Suggested: <strong>
                            {REVIEW_ACTIONS.find(a => a.value === deal.suggested_action)?.label || deal.suggested_action || '—'}
                        </strong>
                    </div>
                    {deal.review_comment && <div className="qa-reviewed-comment">“{deal.review_comment}”</div>}
                </div>
            )}
            {/* ACTIONS (full width) */}
            <div className="qa-actions card">
                <div className="qa-lab">Actions</div>
                {quoteUnpriced && canFinalize && (
                    <div className="qa-muted" role="status" style={{ marginBottom: 8 }}>
                        ⚠ This quote has an item with no price — Quote Ready and Consignment are blocked until every item is priced (or re-run pricing). Reject is still allowed.
                    </div>
                )}
                <div className="qa-actions-row">
                    {canFinalize && (<>
                        <button className="btn btn-quote-ready" disabled={actionsDisabled} title={noPriceTitle} onClick={() => (isWarehouseDropoffOp(deal) ? actQuoteReadyWithMessage('') : setQuoteReadyMessageOpen(true))}>{busy === 'quote-ready' ? '…' : 'Quote Ready'}</button>
                        <button className="btn btn-consignment" disabled={actionsDisabled} title={noPriceTitle} onClick={actConsignment}>{busy === 'consignment' ? '…' : 'Consignment'}</button>
                    </>)}
                    {!isReviewer && (
                        <button className="btn btn-fairprice" disabled={fairPriceDisabled}
                            title={askedFairPrice ? 'A fair price has already been asked' : (askOpen && askedMoreInfo ? 'A more info request is still open' : 'Ask the seller what they think is fair')}
                            onClick={actAskFairPrice}>{busy === 'fairprice' ? '…' : 'Ask for fair price'}</button>
                    )}
                    {canFinalize && (
                        <button className="btn btn-reject" disabled={navDisabled} onClick={() => setRejectOpen(true)}>Reject</button>
                    )}
                    {!isReviewer && (
                        <button className="btn btn-moreinfo" disabled={moreInfoDisabled}
                            title={askedMoreInfo ? 'More info has already been requested' : (askOpen && askedFairPrice ? 'A fair price request is still open' : 'Ask the seller for more information')}
                            onClick={() => setMoreInfoOpen(true)}>More Info</button>
                    )}
                    {canFinalize && (
                        <button className="btn btn-duplicate" disabled={navDisabled} onClick={actDuplicate}>{busy === 'duplicate' ? '…' : 'Duplicate'}</button>
                    )}
                    <span className="qa-sep" />
                    <button className="btn btn-accent" disabled={navDisabled} onClick={() => setRepriceAsk({ scope: 'quote' })}>{busy === 'reprice-quote' ? 'Repricing…' : '⟲ Reprice quote'}</button>
                    <button className="btn btn-secondary" onClick={() => setNotesOpen(true)}>Add Note</button>
                    <span className="qa-sep" />
                    {isReviewer && (<>
                        <button className="btn btn-quote-ready" disabled={navDisabled} onClick={actReviewDone}>{busy === 'review-done' ? '…' : 'Review done'}</button>
                        <button className="btn btn-secondary" disabled={navDisabled} onClick={openAssignDialog}>Reassign</button>
                    </>)}
                    {canFinalize && (
                        <button className="btn btn-secondary" disabled={navDisabled} onClick={openAssignDialog}>Assign for review</button>
                    )}
                    {!isReviewer && (
                        <button className="btn btn-secondary" disabled={navDisabled} onClick={() => setSubmissionOpen(true)}>Customer's submission</button>
                    )}
                    {!isReviewer && items.length > 1 && (
                        <button className="ask-btn" disabled={navDisabled}
                            title="Ask ChatGPT for a second-opinion price on every item on this quote"
                            onClick={openSecondOpinionAll}>
                            {busy === 'second-opinion-all'
                                ? `Starting…${secondOpinionProgress ? ` ${secondOpinionProgress.done}/${secondOpinionProgress.total}` : ''}`
                                : '🤖 Second opinion — all items'}
                        </button>
                    )}
                </div>
            </div>

            <VerdictNotePanel notes={verdictNotes} error={verdictNotesError} signalsById={signalsById} />

            <CustomerNotePanel notes={submission.submission?.notes} />

            <OpAnswersPanel deal={deal} />

            {/* PHOTOS (full-width strip) */}
            <div className="qa-photos-strip card">
                <div className="qa-lab">Photos</div>
                {images.length === 0 && <div className="qa-muted">No photos.</div>}
                {images.length > 0 && (
                    <div className="qa-strip">
                        {images.map((src, i) => (
                            <img key={i} className="qa-strip-thumb" src={src} alt={`photo ${i}`} onClick={() => setLightbox(i)} />
                        ))}
                    </div>
                )}
            </div>

            {/* BUYING RULES (between photos and items, spec req 3): recommendations + why first, warnings below */}
            <BuyingRulesPanel hits={rules.hits} configError={rules.configError} onJumpToItem={jumpToItem}
                items={items} itemCategories={rowCategories} itemSubCategories={rowSubCategories}
                itemSignals={rowSignals} config={rules.config} ready={rules.ready} />

            {/* BODY: Items | Pricing */}
            <div className="qa-body">
                {/* ITEM SPINE */}
                <div className="qa-col qa-spine card">
                    <div className="qa-spine-head">
                        <span className="qa-lab">Items ({items.length})</span>
                        {totalsChip}
                    </div>
                    {items.map((it, idx) => {
                        // Every per-item lookup keys on the item's id: these maps are
                        // answered that way precisely because a row position stops
                        // naming the same item once a line has been removed.
                        const urls = getSearchUrls(it.name, deal.executed_search_terms?.[it.quote_item_id]);
                        const sel = !!it.quote_item_id && it.quote_item_id === selectedId;
                        // This item's OWN grade, never the quote's. `deal.condition`
                        // is derived from the FIRST item (getDealViaPrism), so
                        // falling back to it painted item 1's grade onto every
                        // ungraded item below it. The operator saw a graded line,
                        // and then reprice refused it with "no graded condition"
                        // — the price tier is keyed on the item's own condition,
                        // so the refusal was right and the badge was the lie.
                        const ec = canonCond(it.condition);
                        const isEdit = (field) => edit && edit.idx === idx && edit.field === field;
                        // An agreed price is final, and so is its condition (spec 20261021).
                        const agreed = isAgreedPrice(deal, it);
                        // The KG identity card for this item, if identify has answered.
                        // It also carries the customer's submission photos Prism grouped
                        // to this item (card.photos), rendered just below the card.
                        const idCard = identifications.cardsById[it.quote_item_id] ?? null;
                        return (
                            <div key={it.quote_item_id ?? idx} ref={el => { itemRefs.current[idx] = el; }} className={`parsed-item-card ${sel ? 'selected' : ''}`} onClick={() => setSelectedItemId(it.quote_item_id)}>
                                <div className="parsed-item-main">
                                    <span className="parsed-item-number qa-num-click" title="Show this item's pricing receipts"
                                        onClick={e => { e.stopPropagation(); setSelectedItemId(it.quote_item_id); }}>{idx + 1}</span>
                                    {it.score != null && (
                                        <span className={`score-badge ${it.score >= 150 ? 'high' : it.score >= 100 ? 'medium' : it.score >= 45 ? 'low' : 'very-low'}`}>{it.score}</span>
                                    )}
                                    {isEdit('condition') ? (
                                        <select className="condition-select" autoFocus value={ec || ''}
                                            onClick={e => e.stopPropagation()}
                                            onBlur={() => setEdit(null)}
                                            onChange={e => { const v = e.target.value || null; setEdit(null); withBusy('edit', () => saveCondition(itemIdOf(idx), v)); }}>
                                            <option value="">None</option>
                                            {ITEM_CONDITION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                        </select>
                                    ) : agreed ? (
                                        // The grade is part of the agreement; Prism refuses a change too.
                                        <span className={`qa-cond badge badge-sm ${ec ? getConditionClass(ec) : 'badge-neutral'}`}
                                            title="The price was agreed at this condition. Final.">
                                            {ec ? displayCondition(ec) : 'cond'}
                                        </span>
                                    ) : (
                                        <span className={`qa-cond badge badge-sm editable ${ec ? getConditionClass(ec) : 'badge-neutral'}`}
                                            onClick={e => { e.stopPropagation(); setEdit({ idx, field: 'condition' }); }}>
                                            {ec ? displayCondition(ec) : 'cond'}
                                        </span>
                                    )}
                                    <CategoryPicker main={itemCategories[it.quote_item_id] || null} sub={itemSubCategories[it.quote_item_id] || null} source={itemCategorySources[it.quote_item_id] || null} onChange={(m, s) => changeItemCategory(idx, m, s)} />
                                    <ItemEconomicsBadges item={it} mainCategory={itemCategories[it.quote_item_id] || null} subCategory={itemSubCategories[it.quote_item_id] || null} config={rules.config} />
                                    {it.source === 'pricing' && (
                                        <span className="badge badge-sm badge-warning"
                                            title="Added by the pricing engine from the submission photos — the customer did not declare this line. It sits Ungraded at £0 until you grade it; reject it if it should not be on the quote.">
                                            ⚙ Engine added
                                        </span>
                                    )}
                                    {isEdit('name') ? (
                                        // Brand and model are stored separately and are what the
                                        // pricing search reads. Editing them as one string would
                                        // mean splitting it back, which has no right answer for
                                        // "Bowers & Wilkins CM5".
                                        <span className="item-identity-edit" onClick={e => e.stopPropagation()}>
                                            <input autoFocus className="description-input identity-brand" placeholder="Brand" value={edit.brand}
                                                onChange={e => setEdit({ ...edit, brand: e.target.value })}
                                                onBlur={onIdentityBlur}
                                                onKeyDown={e => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') setEdit(null); }} />
                                            <input className="description-input identity-model" placeholder="Model" value={edit.model}
                                                onChange={e => setEdit({ ...edit, model: e.target.value })}
                                                onBlur={onIdentityBlur}
                                                onKeyDown={e => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') setEdit(null); }} />
                                            {/* Colour is NOT an identity field, so editing it keeps the
                                                item's price tiers — the one box here that costs nothing. */}
                                            <input className="description-input identity-colour" placeholder="Colour" value={edit.colour}
                                                onChange={e => setEdit({ ...edit, colour: e.target.value })}
                                                onBlur={onIdentityBlur}
                                                onKeyDown={e => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') setEdit(null); }} />
                                            {/* is_pair is not an identity field either: the "Pair"
                                                segment of the title renders from this flag, and
                                                toggling it keeps the item's price tiers. */}
                                            <label className="identity-pair">
                                                <input type="checkbox" checked={edit.pair}
                                                    onChange={e => setEdit({ ...edit, pair: e.target.checked })}
                                                    onBlur={onIdentityBlur}
                                                    onKeyDown={e => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') setEdit(null); }} />
                                                Pair
                                            </label>
                                        </span>
                                    ) : (
                                        // The full `display_name`, Prism's one spelling of the item.
                                        // The category inside it is NOT edited here — that is the
                                        // CategoryPicker above; this opens brand, model and colour.
                                        <span className="parsed-item-description editable"
                                            title="Edit brand, model, colour and the Pair flag — changing brand or model clears this item's prices, so reprice after. Category is set with the picker."
                                            onClick={e => { e.stopPropagation(); setEdit(openNameEdit(idx, it)); }}>
                                            {it.display_name || it.name}
                                        </span>
                                    )}
                                </div>
                                <div className="parsed-item-prices">
                                    <LinePrices qty={it.qty} sp={it.sp} bp={it.bp}
                                        qtyCell={(
                                            isEdit('qty') ? (
                                                <input type="number" autoFocus className="price-input" value={edit.value}
                                                    onClick={e => e.stopPropagation()}
                                                    onChange={e => setEdit({ ...edit, value: e.target.value })}
                                                    onBlur={saveQty} onKeyDown={e => { if (e.key === 'Enter') saveQty(); if (e.key === 'Escape') setEdit(null); }} />
                                            ) : (
                                                <span className="price-value editable"
                                                    onClick={e => { e.stopPropagation(); setEdit({ idx, field: 'qty', value: String(it.qty) }); }}>{it.qty}</span>
                                            )
                                        )}
                                        spCell={(
                                            isEdit('sp') ? (
                                                // text, not number: the box accepts the "100/41" pair, whose
                                                // "/" a number input's keystroke filter would swallow. The
                                                // placeholder is words — example numbers read as a prefilled
                                                // value (see DealDetailModal's price box).
                                                <input type="text" inputMode="decimal" placeholder={it.sp == null && it.bp == null ? 'Sell/Buy' : undefined}
                                                    autoFocus className="price-input" value={edit.value}
                                                    onClick={e => e.stopPropagation()}
                                                    onChange={e => setEdit({ ...edit, value: e.target.value })}
                                                    onBlur={() => editSp(idx, edit.value)} onKeyDown={e => { if (e.key === 'Enter') editSp(idx, edit.value); if (e.key === 'Escape') setEdit(null); }} />
                                            ) : agreed ? (
                                                <span className="price-value price-sp" title="Agreed with the customer. Final.">£{money(it.sp)}</span>
                                            ) : (
                                                <span className="price-value price-sp editable"
                                                    onClick={e => { e.stopPropagation(); setEdit({ idx, field: 'sp', value: String(it.sp ?? '') }); }}>£{money(it.sp)}</span>
                                            )
                                        )}
                                        bpCell={(
                                            isEdit('bp') ? (
                                                <input type="number" autoFocus className="price-input" value={edit.value}
                                                    onClick={e => e.stopPropagation()}
                                                    onChange={e => setEdit({ ...edit, value: e.target.value })}
                                                    onBlur={() => editBp(idx, edit.value)} onKeyDown={e => { if (e.key === 'Enter') editBp(idx, edit.value); if (e.key === 'Escape') setEdit(null); }} />
                                            ) : agreed ? (
                                                <span className="price-value price-bp" title="Agreed with the customer. Final.">£{money(it.bp)}</span>
                                            ) : (
                                                <span className="price-value price-bp editable"
                                                    onClick={e => { e.stopPropagation(); setEdit({ idx, field: 'bp', value: String(it.bp ?? '') }); }}>£{money(it.bp)}</span>
                                            )
                                        )}
                                    />
                                    <TheirPrice cents={it.quote_item_id ? shortfallAsks.get(it.quote_item_id) : undefined} bp={it.bp} />
                                </div>
                                <WarehouseSuggestion spCents={it.warehouse_sp_cents} bpCents={it.warehouse_bp_cents} agreed={agreed} engine={rowEngine[idx]} />
                                <div className="parsed-item-actions" onClick={e => e.stopPropagation()}>
                                    <button className="search-btn reprice-btn" disabled={!!busy} title="Re-price this item" onClick={() => setRepriceAsk({ scope: 'item', pos: idx })}>{busy === `reprice-${idx}` ? '...' : '£'}</button>
                                    <button className="search-btn" title="Google" onClick={() => openWin(urls.google)}>G</button>
                                    <button className="search-btn" title="eBay Sold" onClick={() => openWin(urls.ebaySold)}>eS</button>
                                    <button className="search-btn" title="eBay Active" onClick={() => openWin(urls.ebayActive)}>eA</button>
                                    <button className="search-btn" title="Reverb Sold" onClick={() => openWin(urls.reverbSold)}>rS</button>
                                    <button className="search-btn" title="Reverb Active" onClick={() => openWin(urls.reverbActive)}>rA</button>
                                    <button className="search-btn" title="Past items" onClick={() => setPastTitle(it.name)}>🔍</button>
                                    <button className="search-btn" title="Reverse-image lookup" onClick={() => setRevLookupItem(it)}>📷</button>
                                    <button className="search-btn delete-btn" disabled={!!busy} title="Remove item"
                                        onClick={() => setConfirmAsk({
                                            title: `Remove item ${idx + 1}?`, message: it.name, confirmLabel: 'Remove', danger: true,
                                            onConfirm: () => removeItem(idx),
                                        })}>×</button>
                                </div>
                                {/* The KG identity card sits UNDER the prices and the action
                                    buttons: the operator's price line stays where it always was
                                    and the shadow's answer reads as an addendum to the item. */}
                                {/* Hidden for a Deals Reviewer (Med-1): the KG verdict is an
                                    admin identity action; reviewers change make/model via the
                                    item name field instead. */}
                                {idCard && !isReviewer && (
                                    <IdentityCard
                                        card={idCard}
                                        item={it}
                                        disabled={!!busy}
                                        onVerdict={onVerdict}
                                        onOpenProduct={isAdmin ? setOpenProductId : null}
                                    />
                                )}
                                {/* The customer's own photos Prism grouped to THIS item, below
                                    the KG block. Empty (or absent card) renders nothing. */}
                                {idCard?.photos?.length > 0 && <ItemPhotos photos={idCard.photos} />}
                            </div>
                        );
                    })}
                    <button className="qa-additem" disabled={!!busy} onClick={() => setAddItemOpen(true)}>+ Add item</button>
                </div>

                {/* RECEIPTS */}
                <div className="qa-col qa-receipts card">
                    <FairPricePanel data={fairPrice} items={items} />
                    <CustomerMessagesPanel dealId={deal.id} quoteId={deal.quote_id} items={items} />
                    <div className="qa-lab">Pricing receipts</div>
                    <div className="qa-chips">
                        {items.map((it, idx) => (
                            <button key={it.quote_item_id ?? idx}
                                className={`qa-chip ${it.quote_item_id && it.quote_item_id === selectedId ? 'qa-chip-on' : ''}`}
                                onClick={() => setSelectedItemId(it.quote_item_id)}>
                                {idx + 1}. {it.name.slice(0, 14)}
                            </button>
                        ))}
                    </div>
                    {/* The receipts are addressed by the item's id. Without one there is
                        nothing to look them up by, so the operator is told rather than
                        shown a panel fetched under a guessed row position — which is the
                        very mistake that served one item's receipts for another. */}
                    {selectedItem && !selectedId && (
                        <div className="qa-error">
                            Pricing receipts can&apos;t be shown for this item — its identity did not come
                            through with the quote. Reload the page.
                        </div>
                    )}
                    {selectedId && (
                        <>
                            <MarketCompsPanel dealId={deal.id} quoteItemId={selectedId} refreshKey={receiptsVersion}
                                itemName={selectedDisplayName}
                                onExclusionsChanged={() => setExclusionsVersion(v => v + 1)} />
                            <BpBreakdownPanel dealId={deal.id} quoteItemId={selectedId} sp={selectedItem.sp} bp={selectedItem.bp} condition={selectedItem.condition ?? null} refreshKey={receiptsVersion} signals={selectedSignals} />
                            <SecondOpinionPanel dealId={deal.id} quoteItemId={selectedId} sp={selectedItem.sp} onSkipNext={queue.skip}
                                searchTerm={selectedSearchTerm}
                                displayName={selectedDisplayName} refreshKey={secondOpinionVersion} />
                        </>
                    )}
                </div>
            </div>

            {typeof busy === 'string' && busy.startsWith('reprice-') && (
                <div className="qa-overlay">
                    <div className="qa-overlay-box">
                        <div className="qa-spinner" />
                        <div>
                            {busy === 'reprice-quote'
                                ? `Repricing quote…${repriceProgress ? ` (item ${Math.min(repriceProgress.done + 1, repriceProgress.total)} of ${repriceProgress.total})` : ''}`
                                : `Repricing item ${Number(busy.slice('reprice-'.length)) + 1}…`}
                        </div>
                    </div>
                </div>
            )}

            {pairGate && (
                <PairConfirmDialog items={pairGate.items} busy={busy === 'pair-confirm'}
                    onCancel={() => setPairGate(null)} onConfirm={onPairConfirm} />
            )}

            {consignGate && (
                <ConsignmentConfirmDialog itemName={items[0]?.display_name || items[0]?.name}
                    itemCount={items.length}
                    defaultSplit={consignGate.defaultSplit} busy={busy === 'consignment'}
                    loadPreview={loadConsignmentPreview} toEmail={deal.seller_email}
                    onCancel={() => setConsignGate(null)} onConfirm={onConsignmentConfirm} />
            )}

            {submissionOpen && (
                <CustomerSubmissionDialog dealId={deal.id} quoteId={deal.quote_id}
                    submissionState={submission}
                    onClose={() => setSubmissionOpen(false)} />
            )}

            {quoteReadyMessageOpen && (
                <QuoteReadyMessageDialog preview={quoteReadyPreview} toEmail={deal.seller_email}
                    onCancel={() => setQuoteReadyMessageOpen(false)} onConfirm={actQuoteReadyWithMessage} />
            )}

            {confirmOfferOpen && (
                <ConfirmOfferDialog hits={rules.warnHits} busy={busy === 'quote-ready'}
                    onCancel={() => setConfirmOfferOpen(false)} onConfirm={confirmQuoteReady} />
            )}

            {addItemOpen && (
                <AddItemDialog busy={!!busy} onCancel={() => setAddItemOpen(false)} onConfirm={addItem} />
            )}
            {confirmAsk && (
                <ConfirmActionDialog
                    title={confirmAsk.title} message={confirmAsk.message}
                    confirmLabel={confirmAsk.confirmLabel} danger={confirmAsk.danger} busy={!!busy}
                    onCancel={() => setConfirmAsk(null)}
                    onConfirm={() => { const run = confirmAsk.onConfirm; setConfirmAsk(null); run(); }}
                />
            )}

            {repriceAsk && (
                <RepriceDialog
                    scope={repriceAsk.scope}
                    items={(repriceAsk.scope === 'quote' ? items : [items[repriceAsk.pos]])
                        .filter(Boolean)
                        .map(it => ({ name: it.name, lastTerm: deal.executed_search_terms?.[it.quote_item_id] || null }))}
                    onCancel={() => setRepriceAsk(null)}
                    onConfirm={(mode) => {
                        const ask = repriceAsk; setRepriceAsk(null);
                        if (ask.scope === 'quote') repriceQuote(mode); else repriceItem(ask.pos, mode);
                    }}
                />
            )}

            {reviewDoneOpen && (
                <ReviewDoneDialog busy={busy === 'review-done'} onCancel={() => setReviewDoneOpen(false)} onConfirm={onReviewDoneConfirm} />
            )}
            {assignOpen && (
                <AssignReviewDialog title={isReviewer ? 'Reassign quote' : 'Assign for review'} assignees={assignees} busy={busy === 'assign-review'} onCancel={() => setAssignOpen(false)} onConfirm={onAssignConfirm} />
            )}

            {lightbox !== null && <PhotoLightbox images={images} initialIndex={lightbox} onClose={() => setLightbox(null)} />}
            {pastTitle && <PastItemsPopup title={pastTitle} onClose={() => setPastTitle(null)} onOpenDeal={openDealById} />}
            {revLookupItem && (
                <ReverseLookupPopup
                    key={revLookupItem.quote_item_id || 'rev'}
                    item={revLookupItem}
                    images={images}
                    itemPhotos={identifications.cardsById[revLookupItem.quote_item_id]?.photos || []}
                    dealId={deal.id}
                    onClose={() => setRevLookupItem(null)}
                />
            )}
            {openProductId && (
                <ProductDetailModal
                    productModelId={openProductId}
                    onClose={() => setOpenProductId(null)}
                    onMutated={() => setIdentityVersion(v => v + 1)}
                />
            )}
            {verdictAsk && (
                <VerdictApplyDialog
                    workerText={[verdictAsk.card?.worker?.brand, verdictAsk.card?.worker?.model].filter(Boolean).join(' ')}
                    productName={verdictAsk.verdict.product?.display_name}
                    onCancel={() => setVerdictAsk(null)}
                    onRecordOnly={() => { const ask = verdictAsk; setVerdictAsk(null); applyVerdict(ask.item, ask.card, ask.verdict, false); }}
                    onRecordAndUpdate={() => { const ask = verdictAsk; setVerdictAsk(null); applyVerdict(ask.item, ask.card, ask.verdict, true); }}
                />
            )}
            {correctFor && (
                <MergePickerModal
                    sourceProduct={null}
                    searchPath={`/api/deals/${deal.id}/graph-products`}
                    pickLabel="Use"
                    title="Pick the right product"
                    onClose={() => setCorrectFor(null)}
                    onPick={product => {
                        const { item, card } = correctFor;
                        setCorrectFor(null);
                        applyVerdict(item, card, { kind: 'correct', product });
                    }}
                />
            )}
            {openedDeal && (
                <DealDetailModal deal={openedDeal} onClose={() => setOpenedDeal(null)} onDealUpdated={() => setOpenedDeal(null)} />
            )}

            {rejectOpen && (
                <div className="modal-overlay">
                    {/* Fixed width, and a fixed-height preview below: typing a message must not
                        resize the dialog. */}
                    <div className="modal-content" style={{ width: '100%', maxWidth: 640 }} onClick={e => e.stopPropagation()}>
                        <div className="modal-header"><h2>Reject Quote</h2><button className="modal-close" onClick={() => setRejectOpen(false)}>&times;</button></div>
                        <div className="modal-body-scroll">
                            <p>Please confirm why are you rejecting this quote?</p>
                            {REJECT_REASONS.map(reason => (
                                <div key={reason} style={{ marginBottom: 'var(--space-3)' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                        <input type="checkbox" checked={rejectReasons.includes(reason)} onChange={() => toggleRejectReason(reason)} />
                                        {reason}
                                    </label>
                                </div>
                            ))}
                            {!deal.seller_email && (
                                <div className="qa-error qa-banner" role="alert" style={{ marginTop: 'var(--space-4)' }}>
                                    No email address was found for this customer. If you reject now, no email is sent.
                                </div>
                            )}
                            <CustomerMessageBox id="reject-message" value={rejectMessage} onChange={setRejectMessage}
                                preview={rejectPreview} toEmail={deal.seller_email} testId="reject-email-preview"
                                placeholder="Replaces the standard reason in the email. Write full sentences, as 'we'." />
                            <p style={{ fontSize: '0.85em', color: 'var(--color-text-muted)', marginTop: 'var(--space-3)' }}>
                                The selected reasons are <span style={{ color: 'var(--color-error)', fontWeight: 600 }}>recorded internally only</span> — they are not shared with the customer. The email opens with a greeting by name, then the text above.
                            </p>
                        </div>
                        <div className="modal-footer" style={{ padding: 'var(--space-4)', alignItems: 'center' }}>
                            {rejectReasons.length === 0 && <span className="text-muted" style={{ fontSize: 'var(--font-size-sm)', marginRight: 'auto' }}>Tick at least one reason to reject.</span>}
                            <button className="btn btn-secondary" onClick={() => setRejectOpen(false)}>Cancel</button>
                            <button className="btn btn-primary" onClick={submitReject} disabled={rejectReasons.length === 0 || busy === 'reject'}>{busy === 'reject' ? 'Rejecting...' : 'Confirm Reject'}</button>
                        </div>
                    </div>
                </div>
            )}
            {shortfallOpen && (
                <div className="modal-overlay">
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header"><h2>Why can&apos;t we meet their price?</h2><button className="modal-close" onClick={() => setShortfallOpen(false)}>&times;</button></div>
                        <div className="modal-body-scroll">
                            <p>This customer named a price we can&apos;t meet. Pick the reason(s) — the customer sees a short explanation on their collection page.</p>
                            {SHORTFALL_REASONS.map(({ value, label }) => (
                                <div key={value} style={{ marginBottom: 'var(--space-3)' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                        <input type="checkbox" checked={shortfallReasons.includes(value)} onChange={() => toggleShortfallReason(value)} />
                                        {label}
                                    </label>
                                </div>
                            ))}
                        </div>
                        <div className="modal-footer" style={{ padding: 'var(--space-4)' }}>
                            <button className="btn btn-secondary" onClick={() => setShortfallOpen(false)}>Cancel</button>
                            <button className="btn btn-primary" onClick={submitShortfall} disabled={shortfallReasons.length === 0 || busy === 'quote-ready'}>{busy === 'quote-ready' ? 'Sending...' : 'Send Quote Ready'}</button>
                        </div>
                    </div>
                </div>
            )}

            {moreInfoOpen && (
                <div className="modal-overlay">
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header"><h2>Request More Information</h2><button className="modal-close" onClick={() => setMoreInfoOpen(false)}>&times;</button></div>
                        <div className="modal-body-scroll">
                            <p>Select what information is needed:</p>
                            <div style={{ marginBottom: 'var(--space-3)' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                    <input type="checkbox" checked={moreInfoOptions.photosInfo} onChange={() => setMoreInfoOptions(p => ({ ...p, photosInfo: !p.photosInfo }))} />
                                    Photos &amp; detailed info (brand, model)
                                </label>
                            </div>
                            <div style={{ marginBottom: 'var(--space-3)' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                    <input type="checkbox" checked={moreInfoOptions.other} onChange={() => setMoreInfoOptions(p => ({ ...p, other: !p.other }))} />
                                    Other (specify below)
                                </label>
                            </div>
                            {moreInfoOptions.other && (
                                <textarea className="form-input" rows={3} placeholder="Enter the additional information required..." value={otherText} onChange={e => setOtherText(e.target.value)} />
                            )}
                        </div>
                        <div className="modal-footer" style={{ padding: 'var(--space-4)' }}>
                            <button className="btn btn-secondary" onClick={() => setMoreInfoOpen(false)}>Cancel</button>
                            <button className="btn btn-primary" onClick={submitMoreInfo} disabled={busy === 'more-info'}>{busy === 'more-info' ? 'Sending...' : 'Send Request'}</button>
                        </div>
                    </div>
                </div>
            )}

            {notesOpen && (
                <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 'clamp(320px, 32vw, 420px)', background: 'var(--color-bg-primary)', borderLeft: '1px solid var(--color-border)', boxShadow: '-4px 0 16px rgba(0,0,0,0.12)', zIndex: 900, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-2)', padding: 'var(--space-3) var(--space-4)', borderBottom: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)' }}>
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>Notes</div>
                            <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{deal.dealname || `Deal ${deal.id}`}</div>
                        </div>
                        <button className="modal-close" onClick={() => setNotesOpen(false)}>&times;</button>
                    </div>
                    <div style={{ padding: 'var(--space-4)', overflowY: 'auto', flex: 1 }}>
                        <DealNotes dealId={deal.id} />
                    </div>
                </div>
            )}

            {secondOpinionGate && (
                <div className="modal-overlay" onClick={() => { if (!secondOpinionGate.checking) setSecondOpinionGate(null); }}>
                    <div className="modal-content qa-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>🤖 ChatGPT second opinion — all items</h3>
                            {!secondOpinionGate.checking && <button className="modal-close" onClick={() => setSecondOpinionGate(null)}>&times;</button>}
                        </div>
                        <div className="modal-body-scroll">
                            {secondOpinionGate.checking ? (
                                <div className="qa-so-running"><span className="qa-spinner qa-spinner-sm" /> Checking which items still need an opinion…</div>
                            ) : secondOpinionGate.fire.length === 0 ? (
                                <p>Every item already has a second opinion or one is in progress — nothing to research.</p>
                            ) : (
                                <>
                                    <p>This runs a web-search price lookup with <strong>{OPENAI_SECOND_OPINION_MODEL}</strong> for <strong>{secondOpinionGate.fire.length} item{secondOpinionGate.fire.length === 1 ? '' : 's'}</strong>
                                        {secondOpinionGate.skipped > 0 ? <> ({secondOpinionGate.skipped} already done or in progress — skipped)</> : null}:</p>
                                    <ul className="qa-so-all-list">
                                        {secondOpinionGate.fire.map(it => <li key={it.quote_item_id}>{it.name}</li>)}
                                    </ul>
                                    <p>⏱ Each takes <strong>~1–2 minutes</strong> and runs in the background · 💷 <strong>~${(secondOpinionGate.fire.length * 0.25).toFixed(2)}</strong> total (~$0.25 each).<br />Results appear on each item as they finish.</p>
                                </>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" disabled={secondOpinionGate.checking} onClick={() => setSecondOpinionGate(null)}>
                                {secondOpinionGate.fire?.length ? 'Cancel' : 'Close'}
                            </button>
                            {!secondOpinionGate.checking && secondOpinionGate.fire.length > 0 && (
                                <button className="btn btn-primary" onClick={fireSecondOpinionAll}>Yes, ask ChatGPT for {secondOpinionGate.fire.length}</button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
